import classNames from 'classnames';

import { useBundleSpecial } from '@/hooks';
import { SPECIAL_METHOD } from '@/constants';
import { IconSvg, SpecialBundleProgress } from '@/components';

import styles from './specialcounter.module.scss';

interface ISpecialCounter {
  bundleId: string;
}
const SpecialCounter = ({ bundleId }: ISpecialCounter) => {
  const {
    dependentProducts,
    independentProducts,
    processingDependents,
    processingIndependents,
    special,
    currentBundleProducts,
  } = useBundleSpecial({ id: bundleId });

  if (!special) return null;

  if (special.specialType === 'BUNDLE') {
    return (
      <SpecialBundleProgress
        bundleProducts={currentBundleProducts}
        special={special}
      />
    );
  }

  const allowDiscountProducts = special?.allowDiscountProducts;
  const isBogo = special.method === SPECIAL_METHOD.BOGO;
  const activeBuyUnits = isBogo
    ? special.activeBundle?.buyUnits ?? 0
    : independentProducts.length;
  const activeGetUnits = isBogo
    ? special.activeBundle?.getUnits ?? 0
    : dependentProducts.length;
  const cycleUnitTotal = isBogo
    ? special.independentProductsRequired + special.dependentProductsRequired
    : special.totalProductsRequired;
  const cycleUnitsFilled = activeBuyUnits + activeGetUnits;
  const depReq = Math.max(1, special.dependentProductsRequired);
  const indReq = Math.max(1, special.independentProductsRequired);

  const porcentProgress = (cycleUnitsFilled * 100) / cycleUnitTotal;

  const getStepCompleted = isBogo
    ? activeBuyUnits >= indReq && activeGetUnits >= depReq
    : special?.totalProductsRequired ===
      dependentProducts.length + independentProducts.length;

  const dividerShowsCompletedProgress = isBogo
    ? !!special.activeBundleHasPendingGet
    : getStepCompleted;

  return (
    <div
      className={classNames(styles.counter, {
        [styles['counter--allowDiscount']]: allowDiscountProducts,
        [styles['counter--active']]: !processingIndependents,
      })}
    >
      {!allowDiscountProducts && (
        <>
          <div
            className={classNames(styles.step, {
              [styles['step--process']]: processingIndependents,
              [styles['step--completed']]: !processingIndependents,
            })}
          >
            <IconSvg name="shopping-specials" />
            {activeBuyUnits > 0 && (
              <span className={styles.counter}>{activeBuyUnits}</span>
            )}
          </div>
          <span
            className={classNames(styles.divider, {
              [styles['divider--process']]: !processingIndependents,
            })}
            style={{
              backgroundImage: dividerShowsCompletedProgress
                ? `linear-gradient(to right, var(--progress-color-completed) ${porcentProgress}%, transparent 0%)`
                : `linear-gradient(to right, var(--progress-color) ${porcentProgress}%, transparent 0%)`,
            }}
          >
            <IconSvg name={!processingDependents ? 'unlocked' : 'locked'} />
          </span>
        </>
      )}
      <div
        className={classNames(styles.step, {
          [styles['step--process']]: processingDependents,
          [styles['step--completed']]: getStepCompleted,
        })}
      >
        <IconSvg name="offer" />
        {activeGetUnits > 0 && (
          <span className={styles.counter}>{activeGetUnits}</span>
        )}
      </div>
    </div>
  );
};

export default SpecialCounter;
